import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Trash2, Eye, Search, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDate } from '@/lib/utils';
import SmartPagination from '@/components/SmartPagination';
import { getMembershipTierLabel, getMembershipTierStyle, getMembershipStatusLabel } from '@/lib/membership-utils';
import SellerCountryBadge from '@/components/admin/SellerCountryBadge';

const UsersTab = ({
  users,
  filteredUsers,
  userRoleFilter,
  setUserRoleFilter,
  userSearchQuery,
  setUserSearchQuery,
  selectedUsers,
  setSelectedUsers,
  handleSelectUser,
  handleSelectAllUsers,
  isAllSelected,
  isSomeSelected,
  handleRoleChange,
  handleViewUserDetail,
  setDeleteDialog,
  setBulkDeleteDialog,
  hasPermission
}) => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'tr').split('-')[0];
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const paginatedUsers = filteredUsers.slice((page - 1) * pageSize, page * pageSize);

  // Reset to page 1 when filter changes
  React.useEffect(() => { setPage(1); }, [userRoleFilter, userSearchQuery]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CardTitle>
                {t('admin.allUsers')} {userRoleFilter !== 'all' && `(${userRoleFilter})`}
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({filteredUsers.length} {t('admin.results') || 'sonuç'})
                </span>
              </CardTitle>
              {selectedUsers.length > 0 && hasPermission('users.delete') && (
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => setBulkDeleteDialog(true)}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t('admin.deleteSelected') || 'Seçilenleri Sil'} ({selectedUsers.length})
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{t('admin.sellers.filterByStatus')}:</span>
              <Select value={userRoleFilter} onValueChange={(val) => { setUserRoleFilter(val); setSelectedUsers([]); }}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.sellers.all')}</SelectItem>
                  <SelectItem value="buyer">{t('admin.buyers')}</SelectItem>
                  <SelectItem value="seller">{t('admin.sellersLabel')}</SelectItem>
                  <SelectItem value="admin">{t('admin.admins')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('admin.searchUsers') || 'Ad, e-posta veya telefon ile ara...'}
              value={userSearchQuery}
              onChange={(e) => { setUserSearchQuery(e.target.value); setSelectedUsers([]); }}
              className="pl-10 pr-10"
              data-testid="user-search-input"
            />
            {userSearchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => { setUserSearchQuery(''); setSelectedUsers([]); }}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={isAllSelected}
                  ref={(el) => el && (el.indeterminate = isSomeSelected)}
                  onCheckedChange={handleSelectAllUsers}
                  aria-label="Hepsini seç"
                />
              </TableHead>
              <TableHead>{t('admin.user')}</TableHead>
              <TableHead>{t('auth.email')}</TableHead>
              <TableHead>{t('admin.role')}</TableHead>
              <TableHead>{t('admin.sellers.country')}</TableHead>
              <TableHead>{t('admin.membershipColumn')}</TableHead>
              <TableHead>{t('admin.joined')}</TableHead>
              <TableHead className="text-right">{t('common.edit')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedUsers.map((user, index) => (
              <TableRow 
                key={`user-${index}-${user.id}`} 
                data-testid={`user-row-${user.id}`}
                className={selectedUsers.includes(user.id) ? 'bg-primary/5' : ''}
              >
                <TableCell>
                  <Checkbox
                    checked={selectedUsers.includes(user.id)}
                    onCheckedChange={() => handleSelectUser(user.id)}
                    aria-label={`${user.name} seç`}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">
                        {user.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <p className="font-medium">{user.name}</p>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{user.email}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className="capitalize">{user.role}</Badge>
                </TableCell>
                <TableCell>
                  {user.role === 'seller' ? (
                    <SellerCountryBadge country={user.country} compact />
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      {user.country || '—'}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {user.membership_tier ? (
                    <div className="flex flex-col gap-1">
                      <Badge
                        variant="outline"
                        className={`w-fit text-xs border ${getMembershipTierStyle(user.membership_tier)}`}
                      >
                        {getMembershipTierLabel(user.membership_tier, locale)}
                      </Badge>
                      {user.membership_status && user.membership_status !== 'active' && (
                        <span className="text-[10px] text-muted-foreground">
                          {getMembershipStatusLabel(user.membership_status, t)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">{t('admin.noMembership')}</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {user.created_at ? formatDate(user.created_at) : 'N/A'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {hasPermission('users.edit') && (
                      <Select
                        value={user.role}
                        onValueChange={(value) => handleRoleChange(user.id, value)}
                      >
                        <SelectTrigger className="w-32" data-testid={`role-select-${user.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="buyer">{t('auth.buyer')}</SelectItem>
                          <SelectItem value="seller">{t('auth.seller')}</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleViewUserDetail(user)}
                      data-testid={`view-user-${user.id}`}
                      title={t('admin.sellers.viewDetails') || 'Detayları Gör'}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {hasPermission('users.delete') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteDialog({ open: true, type: 'user', id: user.id, name: user.name })}
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <SmartPagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={filteredUsers.length}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      </CardContent>
    </Card>
  );
};

export default UsersTab;
